/**
 * Order progress indicator — shows tracking step icons + connecting lines.
 *
 * Layout: each step is a row with [icon + line segment] on the left and text on the right.
 * The connecting line renders between icons (not through them).
 */

import {
  CheckCircle2,
  ChefHat,
  Package,
  Truck,
  CircleDot,
} from 'lucide-react'
import type { TrackingStep } from '@/types/order'

/**
 * Get the icon for a tracking step
 */
function getStepIcon(stepId: string, isActive: boolean, isCompleted: boolean) {
  const iconClass = `h-5 w-5 ${
    isCompleted
      ? 'text-green-600'
      : isActive
        ? 'text-primary'
        : 'text-muted-foreground'
  }`

  switch (stepId) {
    case 'confirmed':
      return <CheckCircle2 className={iconClass} />
    case 'preparing':
      return <ChefHat className={iconClass} />
    case 'ready':
      return <Package className={iconClass} />
    case 'out-for-delivery':
      return <Truck className={iconClass} />
    case 'delivered':
    case 'completed':
      return <CheckCircle2 className={iconClass} />
    default:
      return <CircleDot className={iconClass} />
  }
}

interface OrderProgressIndicatorProps {
  steps: TrackingStep[]
  currentStepIndex: number
}

export function OrderProgressIndicator({
  steps,
  currentStepIndex,
}: OrderProgressIndicatorProps) {
  return (
    <div>
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1
        // When the active step is the last one, all steps are done — show everything green
        const allDone = currentStepIndex === steps.length - 1
        const isCompleted = allDone ? index <= currentStepIndex : index < currentStepIndex
        const isActive = allDone ? false : index === currentStepIndex
        const isPending = index > currentStepIndex

        // Line below this icon is green if this step is completed
        const lineGreen = isCompleted

        return (
          <div
            key={step.id}
            className={`flex gap-4 transition-opacity ${
              isPending ? 'opacity-40' : 'opacity-100'
            }`}
          >
            {/* Left column: icon circle + connecting line */}
            <div className="flex flex-col items-center">
              {/* Pulse ring wrapper for active step */}
              <div className="relative shrink-0">
                {isActive && (
                  <span className="absolute inset-0 rounded-full animate-ping-slow bg-primary/20" />
                )}
                <div
                  className={`relative flex h-11 w-11 items-center justify-center rounded-full border-2 transition-all ${
                    isCompleted
                      ? 'border-green-500 bg-green-50 dark:bg-green-500/10'
                      : isActive
                        ? 'border-primary bg-primary/10'
                        : 'border-border bg-background'
                  }`}
                >
                  {getStepIcon(step.id, isActive, isCompleted)}
                </div>
              </div>

              {/* Connecting line to next step */}
              {!isLast && (
                <div
                  className={`w-0.5 flex-1 min-h-4 mt-1.5 mb-1.5 rounded-full transition-colors duration-500 ${
                    lineGreen ? 'bg-green-500' : 'bg-border'
                  }`}
                />
              )}
            </div>

            {/* Right column: text content */}
            <div className={`flex-1 pt-2 ${!isLast ? 'pb-4' : ''}`}>
              <div
                className={`font-semibold ${
                  isCompleted
                    ? 'text-green-700 dark:text-green-400'
                    : isActive
                      ? 'text-foreground'
                      : 'text-muted-foreground'
                }`}
              >
                {step.label}
              </div>
              <div className="text-sm text-muted-foreground mt-0.5">
                {step.description}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
